export type ThemePreference = "system" | "dark" | "light";
export type DensityPreference = "compact" | "comfortable" | "spacious";
export type AccentPreference = "spectral" | "moss" | "pumpkin" | "rose" | "lavender";
export type CatalogProviderId = "animeav1" | "animeflv";
export type SkipIntroOption = 30 | 60 | 85 | 90 | 120;
export type SeekSecondsOption = 5 | 10 | 15 | 30;
export type PlaybackSpeedOption = 0.75 | 1 | 1.25 | 1.5 | 2;

export interface AnimePersonalMeta {
  rating?: number; // 1..10
  notes?: string;
  updatedAt: number;
}

export interface UserPreferences {
  theme: ThemePreference;
  density: DensityPreference;
  accent: AccentPreference;
  defaultProvider: CatalogProviderId;
  autoplayNext: boolean;
  skipIntroSeconds: SkipIntroOption;
  seekSeconds: SeekSecondsOption;
  defaultPlaybackSpeed: PlaybackSpeedOption;
  cinemaModeDefault: boolean;
  username: string;
  avatarId: string;
  recentSearches: string[];
  animeMeta: Record<string, AnimePersonalMeta>;
}

export const SKIP_INTRO_OPTIONS: readonly SkipIntroOption[] = [30, 60, 85, 90, 120];
export const SEEK_SECONDS_OPTIONS: readonly SeekSecondsOption[] = [5, 10, 15, 30];
export const PLAYBACK_SPEED_OPTIONS: readonly PlaybackSpeedOption[] = [0.75, 1, 1.25, 1.5, 2];

export const ACCENT_OPTIONS: readonly { id: AccentPreference; label: string; previewHex: string }[] = [
  { id: "spectral", label: "Cielo Espectral", previewHex: "#7dd3e6" },
  { id: "moss", label: "Musgo Fantasma", previewHex: "#6fbf8f" },
  { id: "pumpkin", label: "Calabaza Sombría", previewHex: "#f2a65a" },
  { id: "rose", label: "Rosa Fantasma", previewHex: "#e06b7a" },
  { id: "lavender", label: "Lavanda Gris", previewHex: "#a3a7c4" },
];

export interface PresetAvatar {
  id: string;
  name: string;
  color: string;
  initial: string;
}

/** Mismos avatares predefinidos que en Android (`ui/profile/Avatars.kt`). */
export const PRESET_AVATARS: readonly PresetAvatar[] = [
  { id: "avatar_tanjiro", name: "Tanjiro", color: "#D32F2F", initial: "T" },
  { id: "avatar_gojo", name: "Gojo", color: "#1976D2", initial: "G" },
  { id: "avatar_luffy", name: "Luffy", color: "#F57C00", initial: "L" },
  { id: "avatar_naruto", name: "Naruto", color: "#FFA000", initial: "N" },
  { id: "avatar_nezuko", name: "Nezuko", color: "#E91E63", initial: "N" },
  { id: "avatar_goku", name: "Goku", color: "#FF5722", initial: "G" },
];

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: "system",
  density: "comfortable",
  accent: "spectral",
  defaultProvider: "animeav1",
  autoplayNext: true,
  skipIntroSeconds: 85,
  seekSeconds: 10,
  defaultPlaybackSpeed: 1,
  cinemaModeDefault: false,
  username: "Otaku",
  avatarId: "avatar_tanjiro",
  recentSearches: [],
  animeMeta: {},
};

const STORAGE_KEY = "animflix.preferences.v1";
const MAX_RECENT_SEARCHES = 12;
const MAX_USERNAME_LENGTH = 30;

function loadPreferences(storage: Storage | null): UserPreferences {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(DEFAULT_PREFERENCES);
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return {
      ...structuredClone(DEFAULT_PREFERENCES),
      ...parsed,
      recentSearches: Array.isArray(parsed.recentSearches) ? parsed.recentSearches.slice(0, MAX_RECENT_SEARCHES) : [],
      animeMeta: parsed.animeMeta && typeof parsed.animeMeta === "object" ? parsed.animeMeta : {},
    };
  } catch {
    return structuredClone(DEFAULT_PREFERENCES);
  }
}

/** Sincroniza las preferencias visuales con el DOM y `<meta name="color-scheme">`. */
export function applyPreferencesToDocument(prefs: UserPreferences, doc: Document | null = typeof document !== "undefined" ? document : null) {
  if (!doc) return;
  const root = doc.documentElement;
  root.dataset.theme = prefs.theme;
  root.dataset.density = prefs.density;
  root.dataset.accent = prefs.accent;
  root.style.setProperty("--density", prefs.density);

  let meta = doc.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (!meta && doc.head) {
    meta = doc.createElement("meta");
    meta.name = "color-scheme";
    doc.head.prepend(meta);
  }
  if (meta) {
    meta.content = prefs.theme === "system" ? "light dark" : prefs.theme;
  }
}

export class PreferencesStore {
  private state: UserPreferences;
  private listeners = new Set<() => void>();

  constructor(
    private readonly storage: Storage | null,
    private readonly now: () => number = Date.now
  ) {
    this.state = loadPreferences(storage);
    applyPreferencesToDocument(this.state);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UserPreferences => this.state;

  private commit(next: UserPreferences) {
    this.state = next;
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(next));
      if (next.theme === "system") {
        this.storage?.removeItem("color-scheme");
      } else {
        this.storage?.setItem("color-scheme", next.theme);
      }
    } catch {
      // Modo privado o sin espacio: se mantiene en memoria.
    }
    applyPreferencesToDocument(next);
    this.listeners.forEach((listener) => listener());
  }

  update(patch: Partial<Omit<UserPreferences, "recentSearches" | "animeMeta">>) {
    const next: UserPreferences = { ...this.state, ...patch };
    if (patch.username !== undefined) {
      const trimmed = patch.username.trim().slice(0, MAX_USERNAME_LENGTH);
      next.username = trimmed || DEFAULT_PREFERENCES.username;
    }
    this.commit(next);
  }

  addRecentSearch(query: string) {
    const clean = query.trim();
    if (clean.length < 2) return;
    const filtered = this.state.recentSearches.filter((q) => q.toLowerCase() !== clean.toLowerCase());
    this.commit({
      ...this.state,
      recentSearches: [clean, ...filtered].slice(0, MAX_RECENT_SEARCHES),
    });
  }

  removeRecentSearch(query: string) {
    this.commit({
      ...this.state,
      recentSearches: this.state.recentSearches.filter((q) => q !== query),
    });
  }

  clearRecentSearches() {
    this.commit({ ...this.state, recentSearches: [] });
  }

  getAnimeMeta(animeUrl: string): AnimePersonalMeta | undefined {
    return this.state.animeMeta[animeUrl];
  }

  setAnimeMeta(animeUrl: string, patch: { rating?: number | null; notes?: string }) {
    if (!animeUrl) return;
    const current = this.state.animeMeta[animeUrl] ?? { updatedAt: this.now() };
    const nextRating =
      patch.rating === null ? undefined : patch.rating !== undefined ? Math.min(10, Math.max(1, Math.round(patch.rating))) : current.rating;
    const nextNotes = patch.notes !== undefined ? patch.notes.slice(0, 1000) : current.notes;
    const nextMeta = { ...this.state.animeMeta };

    if (!nextRating && (!nextNotes || !nextNotes.trim())) {
      delete nextMeta[animeUrl];
    } else {
      nextMeta[animeUrl] = {
        ...(nextRating ? { rating: nextRating } : {}),
        ...(nextNotes && nextNotes.trim() ? { notes: nextNotes } : {}),
        updatedAt: this.now(),
      };
    }
    this.commit({ ...this.state, animeMeta: nextMeta });
  }

  resetDefaults() {
    this.commit({
      ...structuredClone(DEFAULT_PREFERENCES),
      recentSearches: this.state.recentSearches,
      animeMeta: this.state.animeMeta,
    });
  }
}

